/**
 * Billing Service - Manages SaaS billing, farm size, packages, and trials
 */

import firebase from "firebase/compat/app";
import { db } from "../firebase";
import { handleFirestoreError } from "../utils";
import { getAnimals } from "./animalService";
import { 
	calculateRequiredPackage, 
	getPackageById,
	TRIAL_CONFIG,
	isEartagUser 
} from "../../../config/billingConfig";

/**
 * Calculates the current farm size (number of active animals)
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<number>} Number of active animals
 */
export async function getFarmSize(userId) {
	try {
		const animals = await getAnimals(userId);
		
		// Filter out archived/inactive animals
		// Count only animals with valid IDs (non-placeholder or active)
		const activeAnimals = animals.filter(animal => {
			if (!animal.ID) return false;
			
			// Consider archived/inactive animals as not counting toward farm size
			if (animal.Archived === true || animal.Active === "Inactive") {
				return false;
			}
			
			return true;
		});
		
		return activeAnimals.length;
	} catch (error) {
		console.error("[billingService][getFarmSize] - Error:", error);
		handleFirestoreError(error);
		return 0;
	}
}

/**
 * Checks if user is an eartag user (has physical eartags, no billing required)
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<boolean>} True if user is an eartag user
 */
async function isEartagUserById(userId) {
	try {
		const userDoc = await db.collection("Users").doc(userId).get();
		if (!userDoc.exists) {
			return false; // New users default to SaaS (noEartags: true)
		}
		const userData = userDoc.data();
		return isEartagUser(userData);
	} catch (error) {
		console.error("[billingService][isEartagUserById] - Error:", error);
		return false; // Default to SaaS on error
	}
}

/**
 * Updates the farm size in the billing document
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<number>} Updated farm size
 */
export async function updateFarmSizeInBilling(userId) {
	try {
		// Skip billing updates for eartag users
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			console.log('[billingService][updateFarmSizeInBilling] - Skipping for eartag user');
			return 0;
		}
		
		const farmSize = await getFarmSize(userId);
		
		await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing")
			.set({
				FarmSize: farmSize,
				ActiveAnimals: farmSize, // Alias for backwards compatibility
				LastUpdated: firebase.firestore.FieldValue.serverTimestamp()
			}, { merge: true });
		
		return farmSize;
	} catch (error) {
		console.error("[billingService][updateFarmSizeInBilling] - Error:", error);
		handleFirestoreError(error);
		return 0;
	}
}

/**
 * Initializes a free trial for a new user
 * @param {string} userId - The unique identifier for the user
 * @param {string} [selectedTier=null] - Selected tier from signup (optional)
 * @returns {Promise<void>}
 */
export async function initializeFreeTrial(userId, selectedTier = null) {
	try {
		// Skip trial initialization for eartag users
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			console.log('[billingService][initializeFreeTrial] - Skipping for eartag user');
			return;
		}
		
		const now = Date.now();
		const trialEndDate = now + (TRIAL_CONFIG.durationDays * 24 * 60 * 60 * 1000);
		
		// Get or create billing document
		const billingRef = db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing");
		
		const billingDoc = await billingRef.get();
		
		// Only initialize if trial hasn't been set yet
		if (billingDoc.exists && billingDoc.data().TrialStartDate) {
			return; // Trial already initialized
		}
		
		// Try to get selected tier from user document if not provided
		let tierToUse = selectedTier;
		if (!tierToUse) {
			try {
				const userDoc = await db.collection("Users").doc(userId).get();
				if (userDoc.exists) {
					const userData = userDoc.data();
					tierToUse = userData.SelectedBillingTier;
				}
			} catch (error) {
				console.error("[billingService][initializeFreeTrial] - Error fetching user document:", error);
			}
		}
		
		const farmSize = await getFarmSize(userId);
		
		// Use selected tier if available, otherwise calculate based on farm size
		let packageTier;
		if (tierToUse) {
			const pkg = getPackageById(tierToUse);
			packageTier = pkg || calculateRequiredPackage(farmSize);
		} else {
			packageTier = calculateRequiredPackage(farmSize);
		}
		
		await billingRef.set({
			BillingState: "Trial",
			TrialStartDate: now,
			TrialEndDate: trialEndDate,
			IsTrialActive: true,
			SubscriptionStatus: "trial",
			FarmSize: farmSize,
			ActiveAnimals: farmSize, // Backwards compatibility
			PackageTier: packageTier.id,
			SelectedTierAtSignup: tierToUse || null, // Store original selection
			BillingPrice: 0, // Free during trial
			BillingDateStart: now,
			BillingDateEnd: trialEndDate,
			BillingDate: trialEndDate, // Next billing date is end of trial
			CreatedAt: firebase.firestore.FieldValue.serverTimestamp(),
			UpdatedAt: firebase.firestore.FieldValue.serverTimestamp()
		}, { merge: true });
		
		console.log(`[billingService][initializeFreeTrial] - Trial initialized with tier ${packageTier.id}`);
	} catch (error) {
		console.error("[billingService][initializeFreeTrial] - Error:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Checks if user is currently in free trial period
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<boolean>} True if user is in trial period
 */
export async function isTrialActive(userId) {
	try {
		const billingDoc = await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing")
			.get();
		
		if (!billingDoc.exists) {
			return false;
		}
		
		const billingData = billingDoc.data();
		const now = Date.now();
		
		if (!billingData.IsTrialActive || !billingData.TrialEndDate) {
			return false;
		}
		
		return now < billingData.TrialEndDate;
	} catch (error) {
		console.error("[billingService][isTrialActive] - Error:", error);
		return false;
	}
}

/**
 * Checks if user can add more animals based on their package and trial status
 * @param {string} userId - The unique identifier for the user
 * @param {number} currentCount - Current number of animals (optional, will be calculated if not provided)
 * @returns {Promise<{canAdd: boolean, reason?: string, limit?: number, current?: number, warning?: string}>}
 */
export async function canAddAnimal(userId, currentCount = null) {
	try {
		// Always allow for eartag users (skip all billing checks)
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			return { canAdd: true, reason: "eartag_user" };
		}
		
		// Check if in trial period
		const inTrial = await isTrialActive(userId);
		
		if (inTrial) {
			return { canAdd: true, reason: "trial" }; // Unlimited during trial
		}
		
		// Get billing state
		const billingDoc = await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing")
			.get();
		
		if (!billingDoc.exists) {
			// No billing set up, allow addition (will trigger trial initialization)
			return { canAdd: true, reason: "no_billing" };
		}
		
		const billingData = billingDoc.data();
		const packageTier = getPackageById(billingData.PackageTier || "free");
		
		if (!packageTier) {
			return { canAdd: true, reason: "no_package" };
		}
		
		// Get current count if not provided
		const farmSize = currentCount !== null ? currentCount : await getFarmSize(userId);
		
		// For FREE tier, allow adding but warn if exceeding limit
		if (packageTier.id === "free" && farmSize + 1 > packageTier.maxAnimals) {
			return {
				canAdd: true,
				reason: "free_tier_limit_warning",
				warning: "Upgrade recommended",
				limit: packageTier.maxAnimals,
				current: farmSize,
				packageName: packageTier.name
			};
		}
		
		// Check if adding one more would exceed the limit for paid tiers
		if (farmSize + 1 > packageTier.maxAnimals) {
			return {
				canAdd: false,
				reason: "package_limit_exceeded",
				limit: packageTier.maxAnimals,
				current: farmSize,
				packageName: packageTier.name
			};
		}
		
		return { canAdd: true, reason: "within_limit", limit: packageTier.maxAnimals, current: farmSize };
	} catch (error) {
		console.error("[billingService][canAddAnimal] - Error:", error);
		// On error, allow addition (fail open) but log the error
		return { canAdd: true, reason: "error_check_failed" };
	}
}

/**
 * Calculates the required package tier based on current farm size
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Object>} Required package tier configuration
 */
export async function calculateRequiredPackageForUser(userId) {
	try {
		const farmSize = await getFarmSize(userId);
		return calculateRequiredPackage(farmSize);
	} catch (error) {
		console.error("[billingService][calculateRequiredPackageForUser] - Error:", error);
		return calculateRequiredPackage(0);
	}
}

/**
 * Upgrades or updates user's package tier
 * @param {string} userId - The unique identifier for the user
 * @param {string} newTierId - New package tier ID (free, small, medium, large, enterprise)
 * @returns {Promise<void>}
 */
export async function upgradePackage(userId, newTierId) {
	try {
		// Skip for eartag users
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			console.log('[billingService][upgradePackage] - Skipping for eartag user');
			return;
		}
		const newPackage = getPackageById(newTierId);
		if (!newPackage) {
			throw new Error(`Invalid package tier: ${newTierId}`);
		}
		
		const farmSize = await getFarmSize(userId);
		
		// Check if farm size fits the new package
		if (farmSize < newPackage.minAnimals || farmSize > newPackage.maxAnimals) {
			console.warn(`[billingService][upgradePackage] - Farm size ${farmSize} doesn't fit package ${newTierId} (${newPackage.minAnimals}-${newPackage.maxAnimals})`);
		}
		
		const billingRef = db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing");
		
		const billingDoc = await billingRef.get();
		const existingData = billingDoc.exists ? billingDoc.data() : {};
		
		const now = Date.now();
		const billingPeriodEnd = now + (30 * 24 * 60 * 60 * 1000); // 30 days
		
		await billingRef.set({
			...existingData,
			PackageTier: newPackage.id,
			BillingState: newPackage.name,
			BillingPrice: newPackage.monthlyPrice || 0,
			BillingDateStart: now,
			BillingDateEnd: billingPeriodEnd,
			BillingDate: billingPeriodEnd,
			SubscriptionStatus: existingData.IsTrialActive ? "trial" : "active",
			UpdatedAt: firebase.firestore.FieldValue.serverTimestamp()
		}, { merge: true });
		
		console.log(`[billingService][upgradePackage] - User upgraded to ${newPackage.name}`);
	} catch (error) {
		console.error("[billingService][upgradePackage] - Error:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Auto-upgrades package if farm size exceeds current tier
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<boolean>} True if upgrade occurred
 */
export async function autoUpgradeIfNeeded(userId) {
	try {
		// Skip for eartag users
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			return false;
		}
		const billingDoc = await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing")
			.get();
		
		if (!billingDoc.exists) {
			return false;
		}
		
		const billingData = billingDoc.data();
		const inTrial = await isTrialActive(userId);
		
		// Don't auto-upgrade during trial
		if (inTrial) {
			return false;
		}
		
		const farmSize = await getFarmSize(userId);
		const currentPackage = getPackageById(billingData.PackageTier || "small");
		const requiredPackage = calculateRequiredPackage(farmSize);
		
		// Only upgrade if required package is higher tier
		if (requiredPackage.id !== currentPackage.id && 
			getPackageTierLevel(requiredPackage.id) > getPackageTierLevel(currentPackage.id)) {
			await upgradePackage(userId, requiredPackage.id);
			return true;
		}
		
		return false;
	} catch (error) {
		console.error("[billingService][autoUpgradeIfNeeded] - Error:", error);
		return false;
	}
}

/**
 * Helper function to get tier level for comparison
 * @param {string} tierId - Package tier ID
 * @returns {number} Tier level (0=free, 1=small, 2=medium, 3=large, 4=enterprise)
 */
function getPackageTierLevel(tierId) {
	const levels = {
		free: 0,
		small: 1,
		medium: 2,
		large: 3,
		enterprise: 4
	};
	return levels[tierId] || 0;
}

/**
 * Checks if user can downgrade to a specific tier (farm size must fit new tier limits)
 * @param {string} userId - The unique identifier for the user
 * @param {string} newTierId - Target tier ID to check
 * @returns {Promise<{canDowngrade: boolean, reason?: string, farmSize?: number, tierLimit?: number}>}
 */
export async function canDowngradeToTier(userId, newTierId) {
	try {
		// Skip for eartag users
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			return { canDowngrade: true, reason: "eartag_user" };
		}

		const newPackage = getPackageById(newTierId);
		if (!newPackage) {
			return { canDowngrade: false, reason: "invalid_tier" };
		}

		const farmSize = await getFarmSize(userId);
		
		// Check if farm size exceeds new tier's max limit
		if (farmSize > newPackage.maxAnimals && newPackage.maxAnimals !== Infinity) {
			return {
				canDowngrade: false,
				reason: "farm_size_exceeds_limit",
				farmSize,
				tierLimit: newPackage.maxAnimals
			};
		}

		return { canDowngrade: true, reason: "within_limit", farmSize };
	} catch (error) {
		console.error("[billingService][canDowngradeToTier] - Error:", error);
		return { canDowngrade: false, reason: "error_check_failed" };
	}
}

/**
 * Checks if user has billing details (payment method and billing address) set up
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<{hasBillingDetails: boolean, hasPaymentMethod: boolean, hasBillingAddress: boolean}>}
 */
export async function hasBillingDetails(userId) {
	try {
		const billingDoc = await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing")
			.get();

		if (!billingDoc.exists) {
			return {
				hasBillingDetails: false,
				hasPaymentMethod: false,
				hasBillingAddress: false
			};
		}

		const billingData = billingDoc.data();
		const hasPaymentMethod = !!(billingData.PaymentMethod && billingData.PaymentMethod.paymentMethodId);
		const hasBillingAddress = !!(
			billingData.BillingAddress &&
			billingData.BillingAddress.fullName &&
			billingData.BillingAddress.streetAddress &&
			billingData.BillingAddress.city &&
			billingData.BillingAddress.postalCode &&
			billingData.BillingAddress.country
		);

		return {
			hasBillingDetails: hasPaymentMethod && hasBillingAddress,
			hasPaymentMethod,
			hasBillingAddress
		};
	} catch (error) {
		console.error("[billingService][hasBillingDetails] - Error:", error);
		return {
			hasBillingDetails: false,
			hasPaymentMethod: false,
			hasBillingAddress: false
		};
	}
}

/**
 * Validates billing access for a user
 * @param {string} userId - The unique identifier for the user
 * @param {boolean} [checkEmailVerification=true] - Whether to check email verification status
 * @returns {Promise<{hasAccess: boolean, reason?: string, trialActive?: boolean, subscriptionStatus?: string, emailVerified?: boolean}>}
 */
export async function validateBillingAccess(userId, checkEmailVerification = true) {
	try {
		// Always grant access for eartag users (skip all billing checks)
		const isEartag = await isEartagUserById(userId);
		if (isEartag) {
			return { 
				hasAccess: true, 
				reason: "eartag_user",
				subscriptionStatus: "eartag_user"
			};
		}
		// Check email verification if requested
		if (checkEmailVerification) {
			try {
				// Import auth from firebase module
				const { auth } = await import("../../../firebase");
				const currentUser = auth.currentUser;
				
				if (currentUser && currentUser.uid === userId && !currentUser.emailVerified) {
					return {
						hasAccess: false,
						reason: "email_not_verified",
						emailVerified: false
					};
				}
			} catch (error) {
				console.error("[billingService][validateBillingAccess] - Error checking email verification:", error);
				// Continue with billing check even if email check fails
			}
		}
		
		const billingDoc = await db
			.collection("Users")
			.doc(userId)
			.collection("Account")
			.doc("Billing")
			.get();
		
		if (!billingDoc.exists) {
			// No billing record, allow access (will initialize trial on first animal after email verification)
			return { hasAccess: true, reason: "no_billing_record" };
		}
		
		const billingData = billingDoc.data();
		const inTrial = await isTrialActive(userId);
		
		if (inTrial) {
			return { 
				hasAccess: true, 
				reason: "trial_active",
				trialActive: true,
				subscriptionStatus: "trial"
			};
		}
		
		const subscriptionStatus = billingData.SubscriptionStatus || "active";
		
		if (subscriptionStatus === "active" || subscriptionStatus === "trial") {
			return { 
				hasAccess: true, 
				reason: "subscription_active",
				trialActive: false,
				subscriptionStatus
			};
		}
		
		return { 
			hasAccess: false, 
			reason: "subscription_inactive",
			subscriptionStatus
		};
	} catch (error) {
		console.error("[billingService][validateBillingAccess] - Error:", error);
		// Fail open on error
		return { hasAccess: true, reason: "error_check_failed" };
	}
}

